import { useState } from "react"

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@coval/ui/accordion"
import { Alert, AlertDescription, AlertTitle } from "@coval/ui/alert"
import { Button } from "@coval/ui/button"
import { Card, CardContent } from "@coval/ui/card"
import { FormField } from "@coval/ui/form-field"
import { Input } from "@coval/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui/select"
import { SettingRow } from "@coval/ui/setting-row"
import { Switch } from "@coval/ui/switch"

import type { OverageSettingsPort } from "../hooks/useOverageSettingsEditor"
import {
  usePaidOverageReview,
  type PaidOverageReviewPort,
} from "../hooks/usePaidOverageReview"
import {
  currencyFractionDigits,
  minorAmountInput,
} from "../utils/overageSettings"
import { OverageSettingsForm } from "./OverageSettingsPanel"

interface Props {
  scopeKey: string
  surface: "signup" | "quick_enablement"
  port: PaidOverageReviewPort
  settings?: { organizationId: string; port: OverageSettingsPort }
  canCheckAvailability?: boolean
}

function Review({
  scopeKey,
  surface,
  port,
  settings,
  canCheckAvailability = true,
}: Props) {
  const editor = usePaidOverageReview(scopeKey, surface, port)
  const [managing, setManaging] = useState(false)
  const review = editor.review
  const settled = editor.phase === "accepted" || editor.phase === "declined"
  const showSettings = settings !== undefined && (managing || settled)
  const showReviewForm =
    review !== null &&
    !(settings && settled) &&
    editor.phase !== "loading" &&
    editor.phase !== "load-error"
  const canManage =
    settings !== undefined &&
    [
      "review",
      "unavailable",
      "load-error",
      "conflict",
      "rejected",
      "payment-setup",
      "payment-authentication",
    ].includes(editor.phase)
  const disclosure = review && (
    <div className="space-y-2 text-sm text-coval-text">
      <p>Terms version: {review.disclosureVersion}</p>
      <p className="whitespace-pre-wrap">{review.disclosureText}</p>
      <ul className="list-disc pl-5">
        {review.priceLines.map((line) => (
          <li key={line}>{line}</li>
        ))}
      </ul>
      <p>{review.collectionTiming}</p>
      <p>{review.validityLabel}</p>
    </div>
  )
  const pendingPayment =
    editor.phase === "payment-setup" ||
    editor.phase === "payment-authentication"
  return (
    <Card data-testid="paid-overage-review" data-phase={editor.phase}>
      <CardContent className="space-y-4 p-6">
        <h2 className="text-sm font-semibold text-coval-text">
          {surface === "signup" ? "Optional paid overages" : "Paid overages"}
        </h2>
        <p className="text-sm text-coval-text-muted">
          Paid overages let you use more than your plan includes. They’re
          optional.
        </p>
        {!showSettings && (
          <>
            {editor.phase === "loading" && (
              <p role="status">Loading overage options…</p>
            )}
            {editor.phase === "unavailable" && (
              <Alert variant="info">
                <AlertTitle>Paid overages are not available yet</AlertTitle>
                <AlertDescription>
                  You can continue using your plan without paid overages.
                </AlertDescription>
                {canCheckAvailability && (
                  <Button
                    variant="secondary"
                    className="mt-3"
                    onClick={() => void editor.load()}
                  >
                    Check availability
                  </Button>
                )}
              </Alert>
            )}
            {editor.phase === "load-error" && (
              <Alert variant="danger">
                <AlertTitle>Couldn't load overage options</AlertTitle>
                <AlertDescription>
                  Try again to review the latest rates and budget options.
                </AlertDescription>
                <Button variant="secondary" onClick={() => void editor.load()}>
                  Try again
                </Button>
              </Alert>
            )}
            {showReviewForm && (
              <>
                {disclosure}
                <form
                  role="presentation"
                  className="space-y-4"
                  onSubmit={(event) => {
                    event.preventDefault()
                    void editor.decide("accept")
                  }}
                  onKeyDown={(event) => {
                    if (
                      event.key === "Enter" &&
                      event.target instanceof HTMLInputElement &&
                      editor.issues.length > 0
                    ) {
                      event.preventDefault()
                      editor.revealValidation()
                    }
                  }}
                >
                  <FormField
                    label="Overage budget"
                    error={editor.attempted ? editor.budgetError : null}
                  >
                    {(ids) => (
                      <>
                        <Select
                          value={editor.mode}
                          disabled={editor.locked}
                          onValueChange={(value) =>
                            editor.setMode(
                              value === "unlimited" ? "unlimited" : "limited",
                            )
                          }
                        >
                          <SelectTrigger {...ids}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {review.limitedMaximumMinor !== null && (
                              <SelectItem value="limited">
                                Set a spending limit
                              </SelectItem>
                            )}
                            {review.unlimitedOffered && (
                              <SelectItem value="unlimited">
                                Unlimited
                              </SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                        {editor.mode === "limited" && (
                          <Input
                            data-testid="paid-overage-budget"
                            aria-label={`Budget in ${review.currency}`}
                            value={editor.amount}
                            disabled={editor.locked}
                            inputMode="decimal"
                            onChange={(event) =>
                              editor.setAmount(event.target.value)
                            }
                            aria-invalid={
                              editor.attempted && editor.budgetError !== null
                            }
                            aria-describedby={ids["aria-describedby"]}
                          />
                        )}
                        {review.limitedMaximumMinor !== null &&
                          currencyFractionDigits(review.currency) !== null && (
                            <p className="text-sm text-coval-text-muted">
                              Maximum budget:{" "}
                              {minorAmountInput(
                                review.limitedMaximumMinor,
                                review.currency,
                              )}{" "}
                              {review.currency}. You can set this to 0.
                            </p>
                          )}
                      </>
                    )}
                  </FormField>
                  <SettingRow
                    label="I agree to these overage terms"
                    tooltip="Review the rates, budget and billing terms above before agreeing. Your choice applies to future usage only after confirmation."
                  >
                    <Switch
                      data-testid="paid-overage-confirm"
                      aria-label="I agree to these overage terms"
                      checked={editor.confirmed}
                      disabled={editor.locked}
                      onCheckedChange={editor.setConfirmed}
                    />
                  </SettingRow>
                  {editor.attempted && !editor.confirmed && (
                    <p role="alert" className="text-sm text-coval-text-muted">
                      Accept the overage terms to continue.
                    </p>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button
                      data-testid="paid-overage-accept"
                      type="submit"
                      disabled={editor.locked}
                    >
                      {editor.phase === "saving"
                        ? "Saving your choice…"
                        : "Accept paid-overage terms"}
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      data-testid="paid-overage-decline"
                      disabled={!editor.canDecline}
                      onClick={() => void editor.decide("decline")}
                    >
                      Decline paid overages
                    </Button>
                  </div>
                </form>
              </>
            )}
            {editor.phase === "uncertain" && (
              <Alert variant="danger">
                <AlertTitle>Couldn't confirm your choice</AlertTitle>
                <AlertDescription>
                  Try again or check its status before making another choice.
                </AlertDescription>
                <Button
                  data-testid="paid-overage-retry"
                  variant="secondary"
                  onClick={() => void editor.retry()}
                >
                  Try again
                </Button>
                <Button
                  data-testid="paid-overage-recheck"
                  variant="secondary"
                  onClick={() => void editor.recheck()}
                >
                  Check status
                </Button>
              </Alert>
            )}
            {(editor.phase === "unverified" ||
              editor.phase === "checking-decision") && (
              <Alert variant="danger">
                <AlertTitle>Couldn't verify your choice</AlertTitle>
                <AlertDescription>
                  Check the status of your previous choice before making
                  another. Contact Coval if it still cannot be confirmed.
                </AlertDescription>
                <Button
                  variant="secondary"
                  data-testid="paid-overage-recheck"
                  disabled={editor.phase === "checking-decision"}
                  onClick={() => void editor.recheck()}
                >
                  {editor.phase === "checking-decision"
                    ? "Checking status…"
                    : "Check status"}
                </Button>
              </Alert>
            )}
            {(editor.phase === "conflict" || editor.phase === "rejected") && (
              <Alert variant="danger">
                <AlertTitle>Current terms or settings changed</AlertTitle>
                <AlertDescription>
                  Review the latest terms before trying again.
                </AlertDescription>
                <Button variant="secondary" onClick={() => void editor.load()}>
                  Reload current terms
                </Button>
              </Alert>
            )}
            {pendingPayment && (
              <Alert variant="info">
                <AlertTitle>Payment setup needs attention</AlertTitle>
                <AlertDescription>
                  Finish setting up or verifying your payment method, then
                  review and accept the terms again. Paid overages are not
                  active.
                </AlertDescription>
                <Button
                  variant="secondary"
                  data-testid="paid-overage-payment-recheck"
                  onClick={() => void editor.load()}
                >
                  Review after payment setup
                </Button>
              </Alert>
            )}
          </>
        )}
        {editor.phase === "accepted" && (
          <Alert variant="info">
            <AlertTitle>Overage terms accepted</AlertTitle>
            <AlertDescription>
              Reference: {editor.reference}. Paid overages are not active yet.
            </AlertDescription>
          </Alert>
        )}
        {editor.phase === "declined" && (
          <p role="status">
            Paid overages declined. Your subscription is unchanged.
          </p>
        )}
        {canManage && !showSettings && (
          <Button variant="secondary" onClick={() => setManaging(true)}>
            Manage saved preferences
          </Button>
        )}
        {settings && showSettings && (
          <>
            {settled && review && (
              <Accordion type="single" collapsible>
                <AccordionItem value="reviewed-terms">
                  <AccordionTrigger>View reviewed terms</AccordionTrigger>
                  <AccordionContent>{disclosure}</AccordionContent>
                </AccordionItem>
              </Accordion>
            )}
            {!settled && (
              <p className="text-sm text-coval-text-muted">
                Manage your saved preference and spending limit. Paid overages
                are not active yet.
              </p>
            )}
            <OverageSettingsForm
              key={settings.organizationId}
              {...settings}
              onReviewTerms={
                editor.phase === "accepted"
                  ? undefined
                  : () => {
                      setManaging(false)
                      void editor.load()
                    }
              }
            />
          </>
        )}
      </CardContent>
    </Card>
  )
}

export function PaidOverageReviewPanel(props: Props) {
  return <Review key={props.scopeKey} {...props} />
}
